import { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';

const POLL_INTERVAL = 2 * 60 * 1000;
const INACTIVITY_LIMIT = 10 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'click', 'keydown', 'scroll', 'touchstart'];

function useUpdateChecker() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const initialBuildTime = useRef(null);
  const lastActivity = useRef(Date.now());

  useEffect(() => {
    const onActivity = () => { lastActivity.current = Date.now(); };
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, onActivity));
  }, []);

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`);
        if (!res.ok) return;
        const { buildTime } = await res.json();

        if (!initialBuildTime.current) {
          initialBuildTime.current = buildTime;
          return;
        }

        if (buildTime !== initialBuildTime.current) {
          const inactive = Date.now() - lastActivity.current;
          if (inactive >= INACTIVITY_LIMIT) {
            window.location.reload();
          } else {
            setUpdateAvailable(true);
          }
        }
      } catch (_) {}
    };

    fetchVersion();
    const id = setInterval(fetchVersion, POLL_INTERVAL);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!updateAvailable) return;
    const id = setInterval(() => {
      if (Date.now() - lastActivity.current >= INACTIVITY_LIMIT) {
        window.location.reload();
      }
    }, 10_000);
    return () => clearInterval(id);
  }, [updateAvailable]);

  return updateAvailable;
}

const styles = {
  container: {
    position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
    background: '#1e293b', color: '#f1f5f9',
    borderRadius: 12, padding: '14px 18px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
    display: 'flex', flexDirection: 'column', gap: 8,
    maxWidth: 320, fontFamily: 'inherit', fontSize: 14,
  },
  title: { fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 },
  subtitle: { color: '#94a3b8', fontSize: 13 },
  btn: {
    marginTop: 4, padding: '7px 14px', borderRadius: 8,
    background: '#3b82f6', color: '#fff', border: 'none',
    cursor: 'pointer', fontWeight: 600, fontSize: 13, alignSelf: 'flex-end',
  },
};

export function UpdateNotification() {
  const updateAvailable = useUpdateChecker();
  if (!updateAvailable) return null;

  return (
    <div style={styles.container}>
      <span style={styles.title}><RefreshCw size={16} /> Nueva versión disponible</span>
      <span style={styles.subtitle}>
        La app se actualizará automáticamente cuando no estés usándola.
      </span>
      <button style={styles.btn} onClick={() => window.location.reload()}>
        Recargar ahora
      </button>
    </div>
  );
}
